import { PayloadAction, SerializedError, createSlice } from '@reduxjs/toolkit';

import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { ILoginResponse } from '@lib/core/users/slices/auth';
import { loginApiUrlCreator } from '@lib/core/users/slices/urls';
import { TDevtoolsUser } from '@lib/core/users/types';

export interface IDevtoolsUserState {
  data: undefined | TDevtoolsUser;
  isLoading: boolean;
  error: SerializedError;
}

export const initialState: IDevtoolsUserState = {
  data: undefined,
  error: null,
  isLoading: false,
};

interface ILoginRequestData {
  email: string;
  password: string;
  captchaValue: string;
  isCaptchaDisabled: boolean;
}

export const actionDevtoolsLogin = createTypedAsyncThunk(
  'users/devtoolsUser/actionLogin',
  async ({ email, password, captchaValue, isCaptchaDisabled }: ILoginRequestData) =>
    request({
      body: {
        email,
        password,
        sitekey: isCaptchaDisabled ? undefined : captchaValue,
      },
      isDevtoolsRequest: true,
      method: REQUEST_METHODS.POST,
      url: loginApiUrlCreator(),
    }),
);

export const devtoolsUserSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(
      actionDevtoolsLogin.fulfilled,

      (state, action: PayloadAction<ILoginResponse>) => {
        const { payload: { access_token, refresh_token, user } = {} } = action;

        state.data = {
          ...state.data,
          access_token,
          email: user?.email,
          groups: user?.groups,
          refresh_token,
        };
        state.isLoading = false;
      },
    );
    builder.addCase(actionDevtoolsLogin.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(actionDevtoolsLogin.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });
  },
  initialState,
  name: 'devtoolsUser',
  reducers: {
    actionResetDevtoolsUser: () => initialState,
    actionResetDevtoolsUserProfileId: state => {
      state.data = { ...state.data, identifier: null };
    },
    actionSaveDevtoolsAccessToken: (state, action: PayloadAction<string>) => {
      state.data = { ...state.data, access_token: action.payload };
    },
    actionSetDevtoolsUserProfileId: (state, { payload }: { payload: string }) => {
      state.data = { ...state.data, identifier: payload || null };
    },
  },
});

export const {
  actionResetDevtoolsUser,
  actionResetDevtoolsUserProfileId,
  actionSetDevtoolsUserProfileId,
  actionSaveDevtoolsAccessToken,
} = devtoolsUserSlice.actions;

export default devtoolsUserSlice.reducer;
